/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/*
 * CheckboxDraggableTree is a Custom Tree that uses Ant Design's Tree component.
 * Features include:
 * - Smart Checkboxes
 * - Drag and drop
 * - Right click context menu to delete, copy etc
 * - Persistent state of expanded keys upon page reload/navigation
 */

import {Tree, TreeDataNode, TreeProps} from 'antd';
import React, {Key, useEffect, useState} from 'react';
import {ITreeNode} from '../../pages/CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/LeftPanelComponents/TreeComponent/CoverageRequirementsTreeComponent';
import {DatasetType} from '../../pages/CoverageRequirements/constants';
import {useSearchParamsState} from '../../../hooks/useSearchParamsState';
import {getTreeItemColor} from '../../pages/CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/LeftPanelComponents/TreeComponent/CustomTreeItem';
import {usePostRequest} from '../../../api/helper';
import {coverageRequirementsModifyTreeEndpoint, TREE_OPERATIONS} from '../../pages/CoverageRequirements/ItemDetails/constants';
import toast from 'react-hot-toast';
import {DataNode} from 'antd/es/tree';
import {DragHandle} from '@mui/icons-material';

export interface CheckboxDraggableTreeProps {
  treeType: string; // "Single" or "Merged" Tree
  treeData: any; // JSON representation of tree data
  renderTreeItemLabel: (td: any) => any; // content of each tree item, shows dataset name, color, etc
  treeNodeOnClick?: (_e: any, nodes: any, treeType: string, _treeData: any) => void;
  treeNodeOnContextMenu?: (e: any, nodes: ITreeNode, treeType: string, _treeData: any, datasetType: DatasetType) => void;
  onChangeCheckedNodes?: (nodes: any) => void;
  controlledCheckedNodes?: React.Key[];
  setControlledCheckedNodes?: any
  isMergeDialogUp: boolean;
  checkIfMerging: Function;
  isDeleting: boolean;

  // TODO: Maybe make new (more generic) component? Or change the name of this one.
  hideCheckboxes?: boolean;
  disableDragAndDrop?: boolean;
}


// Helper function to collect all keys from the tree data
const getAllKeys = (data: TreeDataNode[]): React.Key[] => {
  let keys: React.Key[] = [];
  const traverse = (nodes: TreeDataNode[]) => {
    nodes.forEach(node => {
      keys.push(node.key);
      if (node.children) {
        traverse(node.children);
      }
    });
  };
  traverse(data);
  return keys;
};


const CheckboxDraggableTree: React.FC<CheckboxDraggableTreeProps> = React.memo((props: CheckboxDraggableTreeProps) => {
  const [expandedKeys, setExpandedKeys] = useSearchParamsState(props.treeData.studyId, [].toString());
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState<boolean>(true);
  const fetchHandleModifyNode = usePostRequest(coverageRequirementsModifyTreeEndpoint)
  const [canDragandDrop, setCanDragandDrop] = useState<boolean>(true);

  /**
   * Reorders a tree node in the database.
   * @returns A promise that resolves when the node has been reordered.
   */
  const reorderTreeinDb = (
    dropKey: Key,
    dragKey: Key,
    dropPosition: number,
    dropToGap: boolean,
    nodeType: string,
    treeId: string,
    treeType: string,
    gmid: string,
    studyId: string,
  ) => {
    fetchHandleModifyNode({
      "dropKey": dropKey,
      "dragKey": dragKey,
      "dropPosition": dropPosition,
      "dropToGap": dropToGap,
      "action": TREE_OPERATIONS.REORDER,
      "nodeType": nodeType === "" ? "folder" : "dataset",
      "treeId": treeId,
      'treeType': treeType,
      "gmid": gmid,
      studyId: studyId
    }).then((data) => {
      setCanDragandDrop(true)
      setPrevTreeData(gData)
    }).catch((e) => {
      console.error(e)
      toast.error('Failed to move item in tree')
      setGData(prevTreeData); // Revert the move
    })
  }

  const [gData, setGData] = useState<TreeDataNode[]>([
    mapTreeData(props.treeData.tree, props.treeType, props.renderTreeItemLabel)
  ]);
  const [prevTreeData, setPrevTreeData] = useState<any>([
    mapTreeData(props.treeData.tree, props.treeType, props.renderTreeItemLabel)
  ]);

  useEffect(() => {
    const newData = [mapTreeData(props.treeData.tree, props.treeType, props.renderTreeItemLabel)]
    setGData(newData);
    setPrevTreeData(newData);
  }, [props.treeData.tree, props.treeType, props.renderTreeItemLabel]);

  /**
   * Handles drag and drop of tree nodes.
   * @param info - item being dropped
   */
  const onDrop: TreeProps['onDrop'] = (info) => {
    if (!canDragandDrop || props.checkIfMerging() || props.isMergeDialogUp || props.isDeleting) {
      return
    }
    const dropKey = info.node.key; // Target position of drop
    const dragKey = info.dragNode.key; // Starting position key of the node being dragged
    const dropPos = info.node.pos.split('-');
    const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);

    const loop = (
      data: any[],
      key: React.Key,
      callback: (node: TreeDataNode, i: number, data: any[]) => void,
    ) => {
      for (let i = 0; i < data.length; i++) {
        if (data[i].nodeId === key) {
          return callback(data[i], i, data);
        }
        if (data[i].children) {
          loop(data[i].children!, key, callback);
        }
      }
    };
    const data = [...gData];

    // Find dropObject
    let dropObj: any;
    loop(data, dropKey, (item, index, arr) => {
      dropObj = item;
    });
    // Only studies/folders can have children, and items can't be moved outside the study
    if (dropObj.nodeType === 'dataset' && !info.dropToGap ||
      dropObj.nodeType === 'study' && info.dropToGap
    ) {
      return
    } // In Merged Data Sets, only the root folders can be reordered, and not its children
    if (props.treeType === "Merged" &&
      (dropObj.meta?.type === "root" && !info.dropToGap)) {
      return
    }

    // Find dragObject
    let dragObj: TreeDataNode;
    loop(data, dragKey, (item, index, arr) => {
      arr.splice(index, 1);
      dragObj = item;
    });

    if (!info.dropToGap) {
      // Drop ontop of the item, insert as its first child
      loop(data, dropKey, (item) => {
        item.children = item.children || [];
        item.children.unshift(dragObj);
      });
    } else { // dropped in between
      let ar: TreeDataNode[] = [];
      let i: number;
      loop(data, dropKey, (_item, index, arr) => {
        ar = arr;
        i = index;
      });
      if (dropPosition === -1) {
        // Drop on the top of the drop node
        ar.splice(i!, 0, dragObj!);
      } else {
        // Drop on the bottom of the drop node
        ar.splice(i! + 1, 0, dragObj!);
      }
    }
    setGData(data);
    setCanDragandDrop(false);
    const dn: any = info.dragNode;
    const nodeType = dn.nodeType;
    reorderTreeinDb(dropKey, dragKey, dropPosition, info.dropToGap,
      nodeType, props.treeData.id, props.treeType,
      props.treeData.gmid, props.treeData.studyId)
  };

  const onExpand: TreeProps['onExpand'] = (expandedKeysValue) => {
    setExpandedKeys(expandedKeysValue.toString());
    setAutoExpandParent(false);
  };

  const onCheck: TreeProps['onCheck'] = (checkedKeys, info) => {
    if (!props.controlledCheckedNodes) {
      setCheckedKeys(checkedKeys as React.Key[]);
    }

    const treeState = info.checkedNodes.map(({title, style, checkable, ...rest}) => rest);
    if (props.onChangeCheckedNodes) {
      props.onChangeCheckedNodes(treeState);
    }

  };

  const onSelect: TreeProps['onSelect'] = (selectedKeysValue, info) => {
    setSelectedKeys(selectedKeysValue);
    if (props.treeNodeOnClick) {
      props.treeNodeOnClick(info.event, info.node, props.treeType, props.treeData);
    }
  };

  const onRightClick: TreeProps['onRightClick'] = (info: {
    event: React.MouseEvent<Element, MouseEvent>,
    node: any
  }) => {
    if (props.treeNodeOnContextMenu) {
      props.treeNodeOnContextMenu(info.event, info.node, props.treeType, props.treeData,
        props.treeType === "Single" ? "Single" : "Merged");
    }
  }

  return (
    <Tree
      showIcon
      checkable={!props.hideCheckboxes}
      blockNode
      draggable={props.disableDragAndDrop ? false : {
        icon: <DragHandle/>,
        nodeDraggable: (node: DataNode) => {
          const n: any = node;
          const type = n.nodeType;
          if (props.treeType === "Merged") {
            return n.meta?.type === "root"
          }
          return type !== 'study'
        }
      }}
      onDrop={onDrop}
      onExpand={onExpand}
      expandedKeys={expandedKeys.split(',')}
      autoExpandParent={autoExpandParent}
      // defaultExpandAll={true} // TODO: Doesn't work with current controlled state
      onCheck={onCheck}
      checkedKeys={props.controlledCheckedNodes ? props.controlledCheckedNodes : checkedKeys}
      onSelect={onSelect}
      selectedKeys={selectedKeys}
      onRightClick={onRightClick}
      treeData={gData}
      fieldNames={{children: 'children', title: 'title', key: 'nodeId'}}
      rootStyle={{background: 'none'}}
    />
  );
});


/******************************************************************************
 * Helper Functions
 ******************************************************************************/
/**
 * Renders Tree Items
 * @param td - tree data
 * @param treeType - "Single" or "Merged" Tree
 * @param renderTreeItemLabel - function to render tree item contents
 * @returns JS object representing a tree node with its children
 */
export const mapTreeData = (
  td: any, treeType: string,
  renderTreeItemLabel: (td: any) => any) => {
  if (td === undefined) {
    return null
  }
  const children: any[] = []
  if (td.children !== undefined) {
    td.children.forEach((child: any) => {
      children.push(mapTreeData(child, treeType, renderTreeItemLabel))
    })
  }
  const treeNode: any = JSON.parse(JSON.stringify(td));
  treeNode.title = renderTreeItemLabel(td);
  treeNode.style = {color: getTreeItemColor(td).treeItemColor, whiteSpace: 'nowrap'};
  treeNode.children = children;
  treeNode.checkable = !(treeType === "Merged" && (td.dataType !== 'Merge Set' && td.nodeType !== "study"));
  return treeNode
}

export default CheckboxDraggableTree;